import {
  Component,
  OnInit,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
} from "@angular/core";
import {
  UntypedFormBuilder,
  UntypedFormGroup,
  Validators,
} from "@angular/forms";
import { TranslateService } from "@ngx-translate/core";
import { ToastrService } from "ngx-toastr";
import { marker as TRANSLATE_ME } from "@biesbjerg/ngx-translate-extract-marker";
import { CrudService } from "../../../../Services/crud.service";
import { LogedInUserService } from "../../../../Services/loged-in-user.service";
import { Router, ActivatedRoute, Params } from "@angular/router";
import { NgbAccordionConfig } from "@ng-bootstrap/ng-bootstrap";
import { AngularEditorConfig } from "@kolkov/angular-editor";
import { NgbModal, ModalDismissReasons } from "@ng-bootstrap/ng-bootstrap";
import { FormStateService } from "src/app/Services/form.service";
import { GenerateAiContentComponent } from "src/app/Modals/generate-ai-content/generate-ai-content.component";
import { LoadingService } from "src/app/Services/loading.service";

@Component({
  selector: "app-dynamic-workflow-step-form",
  templateUrl: "./dynamic-workflow-step-form.component.html",
  standalone: false,
  styles: [
    `
      ::ng-deep .custom-width-modal .modal-dialog {
        width: 50vw;
        max-width: none;
      }
    `,
  ],
})
export class DynamicWorkflowStepFormComponent implements OnInit, OnChanges {
  @Input() workflowStep;
  // Project details
  @Input() project;
  // screen details
  @Input() screen;
  // Boolean value, either true or false
  @Input() addStep;
  // if, already added workflow then this variable will hold the value of the currentworkflow
  @Input() currentWorkflow = {};
  // selected version
  @Input() version;
  @Input() isLanguageChanged;
  @Input() commonObject;
  closeResult;
  @Output() onLanguageChange = new EventEmitter<any>();
  @Output() formSubmitted: EventEmitter<void> = new EventEmitter<void>();
  @Output() onStepSubmit: EventEmitter<any> = new EventEmitter<any>();
  @Output() sendRestowf = new EventEmitter<boolean>();
  // To have the data, when newly created and submitting the workflow
  @Input() workflowDetails;
  @Input() availableRoles;
  title;
  prompt;
  currentProject;
  currentScreen;
  selectedVersion;
  form: UntypedFormGroup;
  currentWorkflowStep;
  languageList = [];
  submitted = false;
  textError = false;
  projectData;
  isLoading = false;

  // Next Page URL / Element state
  nextPageType: "url" | "element" = "url";
  nextPageUrlValue: string = "";
  nextPageElements: {
    elementIdentifier: string;
    idVal: string; nameVal: string; pathVal: string;
    classVal: string; tagNameVal: string; xPathVal: string;
  }[] = [{ elementIdentifier: "Path", idVal: "", nameVal: "", pathVal: "", classVal: "", tagNameVal: "", xPathVal: "" }];

  readonly npIdentifiers = [
    { value: "Id",      label: "ID - (element by ID)",             placeholder: "#elementId",          valKey: "idVal"      },
    { value: "Name",    label: "Name - (element by name)",         placeholder: "elementName",         valKey: "nameVal"    },
    { value: "Path",    label: "Path - (element by path)",         placeholder: "html > body > ...",   valKey: "pathVal"    },
    { value: "Class",   label: "Class - (element by class)",       placeholder: ".className",          valKey: "classVal"   },
    { value: "TagName", label: "Tag Name - (element by tag name)", placeholder: "div",                 valKey: "tagNameVal" },
    { value: "XPath",   label: "XPath - (element by XPath)",      placeholder: "//button[@id='btn']", valKey: "xPathVal"   },
  ];
  config: AngularEditorConfig = {
    editable: true,
    spellcheck: true,
    height: "15rem",
    minHeight: "5rem",
    placeholder: "Enter text here...",
    translate: "yes",
    toolbarHiddenButtons: [["insertImage", "insertVideo"]],
  };
  constructor(
    private translateService: TranslateService,
    private formBuilder: UntypedFormBuilder,
    private toastr: ToastrService,
    private crudService: CrudService,
    private logedInUserService: LogedInUserService,
    private route: ActivatedRoute,
    config: NgbAccordionConfig,
    private router: Router,
    private modalService: NgbModal,
    private formStateService: FormStateService,
    private loadingService: LoadingService
  ) {
    this.getLanguages();
    this.translateService.setDefaultLang("en");
    let language = localStorage.getItem("language")
      ? localStorage.getItem("language")
      : "en";
    this.translateService.use(language);
  }
  ngOnChanges(changes: SimpleChanges) {
    if (changes["commonObject"]) {
    }
    // throw new Error("Method not implemented.");
  }

  ngOnInit() {
    this.form = this.formBuilder.group({
      element: ["", ""],
      name: ["", Validators.required, ""],
      elementSelector: ["", ""],
      status: ["", ""],
      languageId: [],
      screenManagementId: [],
      projectManagementId: [],
      id: ["", ""],
      nextPageUrl: ["", ""],
      elementData: ["", ""],
      nextPageButton: ["", ""],
      intro: ["", Validators.required, ""],
      displayStep: ["", Validators.required, ""],
      workflowStepType: ["text", Validators.required, ""],
      roles: [],
    });
    this.form.valueChanges.subscribe(() => {
      if (this.form.dirty) {
        this.formStateService.setFormDirty();
      }
    });
    if (
      this.addStep &&
      this.currentWorkflow &&
      this.currentWorkflow["workflowSteps"] &&
      this.currentWorkflow["workflowSteps"].length
    ) {
      this.form.patchValue({
        displayStep: this.currentWorkflow["workflowSteps"].length + 1,
      });
    } else if (this.addStep) {
      this.form.patchValue({
        displayStep: 1,
      });
    }
    this.currentWorkflowStep = Object.assign({}, this.workflowStep);
    this.form.patchValue(this.currentWorkflowStep);
    this.loadNextPageConfig(this.currentWorkflowStep.nextPageUrl);
  }

  private parseNextPageConfig(val: string): { type: string; url?: string; elements?: any[] } {
    if (!val) return { type: "url", url: "" };
    try {
      const obj = JSON.parse(val);
      if (obj && obj.type) return obj;
    } catch (e) {}
    return { type: "url", url: val };
  }

  private emptyNpSlot() {
    return { elementIdentifier: "Path", idVal: "", nameVal: "", pathVal: "", classVal: "", tagNameVal: "", xPathVal: "" };
  }

  private normalizeIdentifier(raw: string): string {
    const map: Record<string, string> = {
      css: "Path", id: "Id", class: "Class", name: "Name",
      xpath: "XPath", tagname: "TagName",
    };
    return map[raw?.toLowerCase()] || raw || "Path";
  }

  private slotValKey(ident: string): keyof ReturnType<typeof this.emptyNpSlot> {
    const m: Record<string, keyof ReturnType<typeof this.emptyNpSlot>> = {
      Id: "idVal", Name: "nameVal", Path: "pathVal",
      Class: "classVal", TagName: "tagNameVal", XPath: "xPathVal",
    };
    return m[ident] || "pathVal";
  }

  private loadNextPageConfig(nextPageUrl: string) {
    const cfg = this.parseNextPageConfig(nextPageUrl);
    if (cfg.type === "element" && cfg.elements && cfg.elements.length) {
      this.nextPageType = "element";
      this.nextPageElements = cfg.elements.map((e) => {
        const slot = this.emptyNpSlot();
        slot.elementIdentifier = this.normalizeIdentifier(e.elementIdentifier);
        slot[this.slotValKey(slot.elementIdentifier)] = e.element || "";
        return slot;
      });
    } else {
      this.nextPageType = "url";
      this.nextPageUrlValue = cfg.url || "";
      this.nextPageElements = [this.emptyNpSlot()];
    }
  }

  private buildNextPageUrlValue(): string {
    if (this.nextPageType === "element") {
      const elements = this.nextPageElements
        .map((slot) => ({ element: slot[this.slotValKey(slot.elementIdentifier)] || "", elementIdentifier: slot.elementIdentifier }))
        .filter((e) => e.element.trim());
      if (!elements.length) return "";
      return JSON.stringify({ type: "element", elements });
    } else {
      const url = (this.nextPageUrlValue || "").trim();
      if (!url) return "";
      return JSON.stringify({ type: "url", url });
    }
  }

  onNextPageTypeChange(type: "url" | "element") {
    this.nextPageType = type;
    if (type === "element" && !this.nextPageElements.length) {
      this.nextPageElements = [this.emptyNpSlot()];
    }
    this.form.markAsDirty();
    this.formStateService.setFormDirty();
  }

  addNextPageElement() {
    if (this.nextPageElements.length < 4) {
      this.nextPageElements.push(this.emptyNpSlot());
      this.form.markAsDirty();
      this.formStateService.setFormDirty();
    }
  }

  removeNextPageElement(index: number) {
    this.nextPageElements.splice(index, 1);
    this.form.markAsDirty();
    this.formStateService.setFormDirty();
  }

  markFormDirty() {
    this.form.markAsDirty();
    this.formStateService.setFormDirty();
  }

  get f() {
    return this.form.controls;
  }

  getLanguages() {
    let self = this;
    self.crudService.get(
      null,
      function (response) {
        self.languageList = response;
      },
      "languages"
    );
  }

  //This is called when save on workflow step is called
  onSubmit() {
    this.submitted = true;
    var self = this;

    if (this.form.value.workflowStepType == "click" && !this.form.value.intro) {
      this.form.patchValue({ intro: "Enter text here..." });
    }
    if (!this.form.value.intro) {
      this.textError = true;
      return;
    } else {
      this.textError = false;
    }

    // stop here if form is invalid
    if (this.form.invalid) return;

    // Serialize next page config into nextPageUrl field
    this.form.patchValue({ nextPageUrl: this.buildNextPageUrlValue() });

    // submit API call

    var wfPayload: any = {};
    wfPayload["publishStage"] = "dev";
    this.crudService.get(
      null,
      (response) => {
        if (
          response.error &&
          response.error.error &&
          response.error.error.message
        ) {
          self.toastr.error(response.error.error.message, "Invalid Operation");
          self.form.markAsPristine();
          self.formStateService.clearFormDirty();
        } else {
          if (
            this.form.value["id"] != undefined &&
            this.form.value["id"] != null &&
            this.form.value["id"].trim() != ""
          ) {
            var payload = self.form.value;
            payload["elementData"] = {
              elementId:
                self.form.value["elementSelector"] == "Id" &&
                self.form.value["element"] != ""
                  ? self.form.value["element"]
                  : "",
              elementClass:
                self.form.value["elementSelector"] == "Class" &&
                self.form.value["element"] != ""
                  ? self.form.value["element"]
                  : "",
              elementPath:
                self.form.value["elementSelector"] == "Path" &&
                self.form.value["element"] != ""
                  ? self.form.value["element"]
                  : "",
              elementTagName:
                self.form.value["elementSelector"] == "Name" &&
                self.form.value["element"] != ""
                  ? self.form.value["element"]
                  : "",
              elementXPath:
                self.form.value["elementSelector"] == "XPath" &&
                self.form.value["element"] != ""
                  ? self.form.value["element"]
                  : "",
            };
            if (payload.id != undefined && payload.id != null) {
              this.loadingService.show();
              this.crudService.update(
                payload,
                (responseWFS) => {
                  if (
                    responseWFS.error &&
                    responseWFS.error.error &&
                    responseWFS.error.error.message
                  ) {
                    self.toastr.error(
                      responseWFS.error.error.message,
                      "Invalid Operation"
                    );
                    self.form.markAsPristine();
                    self.formStateService.clearFormDirty();
                  } else {
                    self.crudService.update(
                      wfPayload,
                      () => {
                        self.currentWorkflowStep = responseWFS;
                        self.toastr.success(
                          "Workflow Step has been updated successfully.",
                          "Success"
                        );
                        self.loadingService.hide();
                      },
                      "currentWorkflowsDetails/" + this.currentWorkflow["id"]
                    );
                  }
                },
                "currentWorkflowDetailsSteps/" + payload.id
              );
            }
          } else {
            var payload = this.form.value;

            if (response.workflowSteps && response.workflowSteps.length) {
              let filteredSteps = response.workflowSteps.filter(
                (step) => step.id !== payload.id
              );
              /* Adding new step if there are any */
              if (filteredSteps.length) {
                filteredSteps.map((item) => {
                  item.id = undefined;
                  item.workflowsId = self.workflowDetails.id;

                  // self.addWorkflowStep(item);
                });

                payload.id = undefined;
                payload.workflowsId = self.workflowDetails.id;
                payload.status = true;
                payload.screenManagementId = self.screen.id;
                payload.projectManagementId = self.project.id;

                payload["elementData"] = {
                  elementId:
                    self.form.value["elementSelector"] == "Id" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementClass:
                    self.form.value["elementSelector"] == "Class" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementPath:
                    self.form.value["elementSelector"] == "Path" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementTagName:
                    self.form.value["elementSelector"] == "Name" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                };
                payload.languageId =
                  payload.languageId || self.project.primaryLanguage;

                self.crudService.post(
                  payload,
                  function (presponse) {
                    if (
                      presponse.error &&
                      presponse.error.error &&
                      presponse.error.error.message
                    ) {
                      self.toastr.error(
                        presponse.error.error.message,
                        "Invalid Operation"
                      );
                      self.form.markAsPristine();
                      self.formStateService.clearFormDirty();
                    } else {
                      self.crudService.update(
                        wfPayload,
                        () => {
                          self.currentWorkflowStep = presponse;
                          // self.formSubmitted.emit();
                          self.toastr.success(
                            "Workflow has been saved successfully.",
                            "Success"
                          );
                          self.loadingService.hide();

                          // self.formSubmitted.emit(workFlowStepsResponse);
                          self.onStepSubmit.emit(presponse);
                          self.form.markAsPristine();
                          self.formStateService.clearFormDirty();
                        },
                        "currentWorkflowsDetails/" + self.currentWorkflow["id"]
                      );
                    }
                  },
                  "currentWorkflowDetailsSteps"
                );

                self.form.markAsPristine();
                self.formStateService.clearFormDirty();
              } else {
                /* If first time adding workflow, then the workflow steps will be added from here. */
                self.loadingService.show();
                var payloadForWfS = self.form.value;
                payloadForWfS["workflowsId"] = self.currentWorkflow["id"];
                payloadForWfS["languageId"] = self.project["primaryLanguage"];
                payloadForWfS["screenManagementId"] = self.screen["id"];
                payloadForWfS["projectManagementId"] = self.project["id"];
                payloadForWfS["status"] = true;
                payloadForWfS["elementData"] = {
                  elementId:
                    self.form.value["elementSelector"] == "Id" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementClass:
                    self.form.value["elementSelector"] == "Class" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementPath:
                    self.form.value["elementSelector"] == "Path" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                  elementTagName:
                    self.form.value["elementSelector"] == "Name" &&
                    self.form.value["element"] != ""
                      ? self.form.value["element"]
                      : "",
                };
                payloadForWfS["roles"] = self.form.value["roles"];
                self.crudService.post(
                  payloadForWfS,
                  (workFlowStepsResponse) => {
                    if (workFlowStepsResponse) {
                      self.crudService.update(
                        wfPayload,
                        () => {
                          self.loadingService.hide();

                          // self.formSubmitted.emit(workFlowStepsResponse);
                          self.onStepSubmit.emit(workFlowStepsResponse);
                        },
                        "currentWorkflowsDetails/" + self.currentWorkflow["id"]
                      );
                    }
                  },
                  "currentWorkflowDetailsSteps"
                );
              }
            } else {
              self.loadingService.show();
              var payloadForWfS = self.form.value;
              payloadForWfS["workflowsId"] = self.currentWorkflow["id"];
              payloadForWfS["languageId"] = self.project["primaryLanguage"];
              payloadForWfS["screenManagementId"] = self.screen["id"];
              payloadForWfS["projectManagementId"] = self.project["id"];
              payloadForWfS["status"] = true;
              payloadForWfS["elementData"] = {
                elementId:
                  self.form.value["elementSelector"] == "Id" &&
                  self.form.value["element"] != ""
                    ? self.form.value["element"]
                    : "",
                elementClass:
                  self.form.value["elementSelector"] == "Class" &&
                  self.form.value["element"] != ""
                    ? self.form.value["element"]
                    : "",
                elementPath:
                  self.form.value["elementSelector"] == "Path" &&
                  self.form.value["element"] != ""
                    ? self.form.value["element"]
                    : "",
                elementTagName:
                  self.form.value["elementSelector"] == "Name" &&
                  self.form.value["element"] != ""
                    ? self.form.value["element"]
                    : "",
              };
              payloadForWfS["roles"] = self.form.value["roles"];
              self.crudService.post(
                payloadForWfS,
                (workFlowStepsResponse) => {
                  if (workFlowStepsResponse) {
                    self.crudService.update(
                      wfPayload,
                      () => {
                        self.loadingService.hide();

                        // self.formSubmitted.emit(workFlowStepsResponse);
                        self.onStepSubmit.emit(workFlowStepsResponse);
                      },
                      "currentWorkflowsDetails/" + self.currentWorkflow["id"]
                    );
                  }
                },
                "currentWorkflowDetailsSteps"
              );
            }
            self.form.markAsPristine();
            self.formStateService.clearFormDirty();
          }
        }
      },
      "currentWorkflowsDetails/" + this.currentWorkflow["id"]
    );

    this.submitted = false;
  }

  addWorkflowStep(payload) {
    var self = this;
    this.loadingService.show();
    this.crudService.post(
      payload,
      function (response) {
        if (
          response.error &&
          response.error.error &&
          response.error.error.message
        ) {
          self.toastr.error(response.error.error.message, "Invalid Operation");
          self.form.markAsPristine();
          self.formStateService.clearFormDirty();
        } else {
          self.loadingService.hide();
          self.toastr.success(
            "Workflow has been saved successfully.",
            "Success"
          );

          self.onStepSubmit.emit(response);
        }
        self.form.markAsPristine();
        self.formStateService.clearFormDirty();
      },
      "currentWorkflowDetailsSteps"
    );
  }

  onReset() {
    this.submitted = false;
    if (this.addStep) {
      this.form.reset();
    } else {
      this.form.patchValue(this.currentWorkflowStep);
    }
    this.form.markAsPristine();
    this.formStateService.clearFormDirty();
  }

  showSelectedLangaugeTooltip(languageId) {
    let self = this;

    if (languageId === self.project.primaryLanguage) {
      // Default primary language case
      this.currentWorkflowStep = Object.assign({}, this.workflowStep);
      this.form.patchValue(this.currentWorkflowStep);
      this.loadNextPageConfig(this.currentWorkflowStep.nextPageUrl);
      self.onLanguageChange.emit({
        id: self.currentWorkflowStep.id,
        name: self.getLanguageName(languageId),
      });
    } else if (languageId !== self.project.primaryLanguage) {
      self.crudService.get(
        null,
        function (response) {
          var isTooltipExistForSelectedLangauge =
            response &&
            response.length &&
            response.find(function (val) {
              return val.languageId === languageId;
            });
          if (isTooltipExistForSelectedLangauge) {
            self.currentWorkflowStep = isTooltipExistForSelectedLangauge;
            self.form.patchValue(isTooltipExistForSelectedLangauge);
            self.loadNextPageConfig(isTooltipExistForSelectedLangauge.nextPageUrl);
            self.onLanguageChange.emit({
              id: self.currentWorkflowStep.parentId,
              name: self.getLanguageName(languageId),
            });
          } else {
            // Copy primary language tooltip with newly/selected language and keep parent id as primary language tooltip
            let newobj = {
              ...self.workflowStep,
              parentId: self.workflowStep.id,
            };
            newobj.languageId = languageId;
            delete newobj.id;
            self.crudService.post(
              newobj,
              function (response) {
                self.toastr.success(
                  "Workflow Step has been created for new language successfully.",
                  "Success"
                );
                self.currentWorkflowStep = response;
                self.form.patchValue(response);
                self.onLanguageChange.emit({
                  id: self.currentWorkflowStep.parentId,
                  name: self.getLanguageName(languageId),
                });
              },
              "currentWorkflowDetailsSteps"
            );
          }
        },
        'currentWorkflowDetailsSteps?filter={"where":{"parentId":"' +
          self.workflowStep.id +
          '"}}'
      );
    }
  }

  getLanguageName(languageId) {
    if (this.languageList.length) {
      var langauge = this.languageList.find(function (val) {
        return val.id === languageId;
      });
      if (langauge) {
        return langauge.name;
      } else {
        return "English";
      }
    }
  }

  open(content) {
    this.modalService.open(content);
  }

  getNewLanguage() {
    var self = this;
    var newlan = [];
    self.languageList.forEach(function (lan) {
      var isExist = self.project.supportedLanguage.find(function (sl) {
        return sl.id === lan.id;
      });
      if (!isExist && lan.id != self.project.primaryLanguage) {
        newlan.push(lan);
      }
    });
    return newlan;
  }

  saveNewSelectedLanguage(newLanguageList) {
    var self = this;
    if (newLanguageList.selectedOptions.selected.length) {
      newLanguageList.selectedOptions.selected.forEach((selectedNewLan) => {
        var isExist = self.languageList.find(function (sl) {
          return sl.name == selectedNewLan.getLabel().trim();
        });
        if (isExist) {
          self.project.supportedLanguage.push(isExist);
        }
      });

      self.crudService.update(
        self.project,
        function (response) {
          if (response) {
            self.project = response;
            self.toastr.success(
              "New language added successfully to the project.",
              "Success"
            );
          }
        },
        "projectManagements"
      );
    }
    self.modalService.dismissAll();
  }
  generateContentForWorkflowStep() {
    this.sendRestowf.emit(true);
    const modalref = this.modalService.open(GenerateAiContentComponent, {
      ariaLabelledBy: "modal-basic-title",
      backdrop: "static",
      keyboard: false,
      windowClass: "top-modal custom-width-modal",
    });
    modalref.componentInstance.title = "Workflow-step";
    modalref.componentInstance.prompt = `You are writing a user-facing description for a step in a business workflow.

    Context:
    - Workflow Name:  ${this.currentWorkflow["name"]}
    - Workflow Description:${
      this.currentWorkflow["description"] &&
      this.currentWorkflow["description"].trim() !== ""
        ? this.currentWorkflow["description"]
        : "(No description provided)"
    }
    - Project Name: ${this.project["name"]}
    - Screen Name: ${this.screen.name}
    - Screen Description: ${
      this.screen["description"] && this.screen["description"].trim() !== ""
        ? this.screen["description"]
        : "(No description provided)"
    }
    - Step Number: [stepNumber] of [totalSteps]
    - Step Purpose: [stepPurpose] (e.g., input details, review, upload docs, verify identity, finalize action, etc.)
    - This step points to or leads into: [nextStepIntent] (e.g., document verification, preview summary, etc.)
   
   `;
    modalref.componentInstance.projectId = this.project.id;
    modalref.componentInstance.screenId = this.screen.id;
    modalref.componentInstance.currentScreen = this.screen;
    modalref.componentInstance.selectedVersion = this.selectedVersion;
    modalref.componentInstance.contentResponseforWfs.subscribe((response) => {
      if (
        response !== undefined &&
        response !== null &&
        typeof response === "string" &&
        response.trim() !== ""
      ) {
        this.form.patchValue({
          intro: response,
        });
        this.form.get("intro")?.markAsDirty();
      }
    });
    modalref.result.then(
      (result) => {
        this.closeResult = `Closed with: ${result}`;
      },
      (reason) => {
        this.closeResult = `Dismissed ${this.getDismissReason(reason)}`;
      }
    );
  }
  private getDismissReason(reason: any): string {
    switch (reason) {
      case ModalDismissReasons.ESC:
        return "by pressing ESC";
      case ModalDismissReasons.BACKDROP_CLICK:
        return "by clicking on a backdrop";
      default:
        return `with: ${reason}`;
    }
  }
}
